import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  FormControl,
  FormLabel,
  Select,
  Alert,
  AlertIcon,
  Spinner,
  VStack,
  useToast,
} from '@chakra-ui/react';

import {
  getPublishToStoreEligibleStoresApi,
  publishSkuToStoreApi,
} from 'features/sku-management/sku-management.api';

const PublishSkuToStoreModal = ({ isOpen, onClose, row, onPublished }) => {
  const dispatch = useDispatch();
  const toast = useToast();

  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [loadingStores, setLoadingStores] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);

  useEffect(() => {
    if (!isOpen || !row?.id) {
      return undefined;
    }

    let cancelled = false;
    setLoadingStores(true);
    setLoadError('');
    setStores([]);
    setSelectedStoreId('');

    dispatch(getPublishToStoreEligibleStoresApi(row.id)).then((result) => {
      if (cancelled) return;
      setLoadingStores(false);
      if (getPublishToStoreEligibleStoresApi.fulfilled.match(result)) {
        const eligible = result.payload?.stores ?? [];
        setStores(eligible);
        if (eligible.length === 1) {
          setSelectedStoreId(eligible[0].storeId);
        }
      } else {
        setLoadError(result.payload?.message || 'Could not load eligible stores');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, isOpen, row?.id]);

  const handlePublish = async () => {
    if (!row?.id || !selectedStoreId) return;
    setIsPublishing(true);
    const result = await dispatch(
      publishSkuToStoreApi({ skuMongoId: row.id, storeId: selectedStoreId }),
    );
    setIsPublishing(false);

    if (publishSkuToStoreApi.fulfilled.match(result)) {
      const storeName =
        stores.find((s) => s.storeId === selectedStoreId)?.storeName ?? 'store';
      toast({
        title: 'Listing created',
        description: `${row.canonicalSku} is now listed on ${storeName}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      onClose();
      onPublished?.();
    } else {
      toast({
        title: 'Could not create listing',
        description: result.payload?.message || 'Publish failed',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const liveStores = row?.storeNames?.length ? row.storeNames.join(', ') : 'none';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>List on another store</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.600">
              Create a new eBay listing for{' '}
              <Text as="span" fontWeight="semibold" color="gray.800">
                {row?.canonicalSku}
              </Text>{' '}
              using this SKU&apos;s catalog data (title, images, specs, and fitment). Listing
              fields configuration (price, quantity, condition, location) and the target
              store&apos;s default eBay policies will be applied.
            </Text>

            <Text fontSize="xs" color="gray.500">
              Currently live on: {liveStores}
            </Text>

            {loadingStores && (
              <VStack py={6} spacing={3}>
                <Spinner size="sm" color="teal.500" />
                <Text fontSize="sm" color="gray.500">
                  Loading stores…
                </Text>
              </VStack>
            )}

            {!loadingStores && loadError && (
              <Alert status="error" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {loadError}
              </Alert>
            )}

            {!loadingStores && !loadError && stores.length === 0 && (
              <Alert status="info" borderRadius="md" fontSize="sm">
                <AlertIcon />
                This SKU is already listed on all available eBay stores.
              </Alert>
            )}

            {!loadingStores && !loadError && stores.length > 0 && (
              <FormControl>
                <FormLabel fontSize="sm">Target store</FormLabel>
                <Select
                  placeholder="Select a store"
                  value={selectedStoreId}
                  onChange={(e) => setSelectedStoreId(e.target.value)}
                >
                  {stores.map((store) => (
                    <option key={store.storeId} value={store.storeId}>
                      {store.storeName}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isPublishing}>
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={handlePublish}
            isLoading={isPublishing}
            loadingText="Creating…"
            isDisabled={!selectedStoreId || loadingStores || stores.length === 0}
          >
            Create listing
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PublishSkuToStoreModal;
